import { Link } from "react-router";
import { Alert, Skeleton } from "@heroui/react";
import { useGetRecipeTypes } from "@/hooks/useGetRecipeTypes";
import { useRecipeTypeCounts } from "@/hooks/useRecipeTypeCounts";
import { getRecipeTypePageMetadata, recipeTypeToPath, RecipeType } from "@/lib/types";
import { EmptyLibraryAlert } from "./EmptyLibraryAlert";

// Curated meal-progression order: meal flow across the top row, accompaniments
// and closers along the bottom. Used to sort the type tiles into a sensible
// reading sequence rather than alphabetical.
const CHAPTER_ORDER: RecipeType[] = [
    RecipeType.Breakfast,
    RecipeType.Appetizer,
    RecipeType.Side,
    RecipeType.Main,
    RecipeType.Soup,
    RecipeType.Bread,
    RecipeType.Snack,
    RecipeType.Dessert,
    RecipeType.SaucesAndSeasonings,
    RecipeType.Drink
];

type DisplayCount = number | "??";

interface ChapterTileProps {
    readonly typeName: RecipeType;
    readonly count: DisplayCount;
}

const ChapterTile = ({ typeName, count }: ChapterTileProps) => {
    const meta = getRecipeTypePageMetadata(typeName);
    const path = recipeTypeToPath(typeName);
    const countLabel =
        count === "??" ? "?? recipes" : `${count} ${count === 1 ? "recipe" : "recipes"}`;

    return (
        <Link
            to={`/recipes/type/${path}`}
            className="group relative aspect-[5/4] overflow-hidden rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300"
        >
            <img
                src={meta.coverImageUrl}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover saturate-[0.85] transition-transform duration-500 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute inset-0 bg-black/10" />
            <div className="relative h-full flex flex-col justify-end p-4 sm:p-5">
                <h3 className="font-Cinzel text-base sm:text-lg uppercase tracking-[0.15em] leading-tight !text-white">
                    <span className="inline-block pb-0.5 border-b border-transparent group-hover:border-[var(--color-accent-400)] transition-colors duration-300">
                        {meta.title}
                    </span>
                </h3>
                <p className="font-serif italic text-xs sm:text-sm text-white/80 mt-1">
                    {countLabel}
                </p>
            </div>
        </Link>
    );
};

const SkeletonTile = () => <Skeleton className="aspect-[5/4] w-full rounded-lg" />;

export const HomeChapters = () => {
    const { data: types, isPending, isError: typesError } = useGetRecipeTypes();
    const { data: counts, isLoading: countsLoading, isError: countsError } = useRecipeTypeCounts();

    const isLoading = isPending || countsLoading;
    const hasError = typesError || countsError;
    const isEmpty =
        !isLoading && !hasError && (!counts || Object.values(counts).every((c) => c === 0));

    const idByName = new Map<string, number>();
    if (types) for (const t of types) idByName.set(t.name, t.id);

    const getCount = (typeName: RecipeType): DisplayCount => {
        if (hasError) return "??";
        const id = idByName.get(typeName);
        if (id == null) return "??";
        return counts?.[id] ?? 0;
    };

    return (
        <section className="bg-[var(--bg)] px-6 sm:px-12 pt-2 sm:pt-3 pb-16 sm:pb-20">
            <div className="m-auto max-w-6xl">
                <header className="mb-6 sm:mb-8 text-center">
                    <h2 className="font-Cinzel text-3xl sm:text-4xl uppercase tracking-[0.2em] text-[var(--color-secondary-500)] mb-3">
                        By Chapter
                    </h2>
                    <p className="font-serif italic text-lg sm:text-xl text-[var(--fg-muted)]">
                        The grimoire's table of contents.
                    </p>
                </header>

                {!isLoading && hasError && (
                    <Alert status="danger" className="mx-auto mb-6 sm:mb-8 max-w-xl">
                        <Alert.Indicator />
                        <Alert.Content>
                            <Alert.Title>Couldn't load recipes.</Alert.Title>
                            <Alert.Description>Please try again later.</Alert.Description>
                        </Alert.Content>
                    </Alert>
                )}

                {!isLoading && !hasError && isEmpty && <EmptyLibraryAlert />}

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
                    {isLoading &&
                        Array.from({ length: 10 }).map((_, i) => <SkeletonTile key={i} />)}

                    {!isLoading &&
                        CHAPTER_ORDER.map((typeName) => (
                            <ChapterTile
                                key={typeName}
                                typeName={typeName}
                                count={getCount(typeName)}
                            />
                        ))}
                </div>
            </div>
        </section>
    );
};
